<script lang="ts">
    import Vue from "vue";
    import { Component } from 'vue-facing-decorator';
    import { SelectionProjectReadModel, SelectionCriterionReadModel, SelectionComparableScenarioReadModel } from "@Quest/PortfolioScenarios/Domain/readmodels";
    import { getDistinctBgCss, getDistinctColor, numCompare } from "@common/Common";
    import { ProjectAcceptance } from "@common/types";
    import CriterionSelectorDialog from "@Criteria/Hierarchies/Components/CriterionSelectorDialog.vue";
    import { CriterionSelectorDialogViewModel } from "@Criteria/Hierarchies/Components/CriterionSelectorDialog";

    @Component({
        components: {
            CriterionSelectorDialog
        },
        props: {
            propCurrencyLabel: { type: String, required: true },
            propProjects: { type: Array, required: true },
            propAllCriteria: { type: Array, required: true },
            propComparableScenarios: { type: Array, required: true },
            propSearchPhrase: { type: String, required: false, default: null },
            propProjectsFilter: { type: String, required: false, default: null },
        }
    })
    export default class ProjectsSelectionView extends Vue
    {
        get currencyLabel(): string { return this.$props.propCurrencyLabel; }
        get projects(): SelectionProjectReadModel[] { return this.$props.propProjects as SelectionProjectReadModel[]; }
        get allCriteria(): SelectionCriterionReadModel[] { return this.$props.propAllCriteria as SelectionCriterionReadModel[]; }
        get comparableScenarios(): SelectionComparableScenarioReadModel[] { return this.$props.propComparableScenarios as SelectionComparableScenarioReadModel[]; }
        get searchPhrase(): string | null { return this.$props.propSearchPhrase; }
        get projectsFilter(): string | null { return this.$props.propProjectsFilter; }
        
        projectsDataView: string = "CBA";
        criteriaFilter: string = "Objectives";
        valueFilter: number = null;
        criterionId: number = null;
        criterionSelectorDialog = new CriterionSelectorDialogViewModel();

        get criteria(): SelectionCriterionReadModel[]
        {
            const filter = this.criteriaFilter;

            if (filter === 'Objectives')
            {
                return this.allCriteria.filter(a => a.IsTop);
            }
            else if (filter === 'Attributes')
            {
                return this.allCriteria.filter(a => a.IsBottom);
            }
            else if (filter === 'All criteria')
            {
                return this.allCriteria.filter(a => !a.IsGoal);
            }

            return [];
        }

        get comparableProjects() { return this.projects.sort((a, b) => numCompare(a.ComparableScenariosAcceptanceValue, b.ComparableScenariosAcceptanceValue)).reverse(); }

        get criteriaFilterVisible() { return this.projectsDataView === "RAG"; }
        get criterionSelectorVisible() { return this.projectsDataView === "Ranking"; }
        get valueFilterVisible() { return this.projectsDataView === "Ranking"; }
        

        get valueFilterLabel(): string
        {
            if (this.valueFilter == null) return 'All';
            else return `Value > ${this.valueFilter}`;
        }

        get selectedCriterion(): SelectionCriterionReadModel { return this.allCriteria.find(a => a.Id == this.criterionId) ?? this.allCriteria.find(a => a.ParentId == null); }

        get rankingProjects()
        {
            let view = new Array(...this.projects);

            if (this.valueFilter != null)
            {
                view = view.filter(a => a.NormalizedCriteriaValues[this.selectedCriterion.ValueIndex] >= this.valueFilter);
            }
            
            return view.sort((a, b) => numCompare(a.NormalizedCriteriaValues[this.selectedCriterion.ValueIndex], b.NormalizedCriteriaValues[this.selectedCriterion.ValueIndex])).reverse();
        }

        get subcriteria(): SelectionCriterionReadModel[]
        {
            return this.allCriteria.filter(a => a.ParentId === this.selectedCriterion.Id);
        }

        get criterionLabel(): string
        {
            return this.selectedCriterion.Name;
        }


        updateSearchPhrase(searchPhrase: string | null)
        {
            this.$emit('update-search-phrase', searchPhrase);
        }

        updateProjectsFilter(filter: string)
        {
            this.$emit('update-projects-filter', filter);
        }


        showCriterionSelector()
        {
            this.criterionSelectorDialog.show(this.criterionId, this.allCriteria);
        }


        formatValue(val: number | null): string
        {
            return val?.toLocaleString('en', { maximumFractionDigits: 1 });
        }

        getRagClass(val: number | null): string
        {
            if (val != null)
            {
                if (val < 25.0)
                {
                    return "table-danger";
                }
                else if (val >= 25.0 && val < 75.0)
                {
                    return "table-warning";
                }
                else if (val >= 75.0)
                {
                    return "table-success";
                }
            }

            return '';
        }

        getCompareAcceptanceClass(val: string | null): string
        {
            if (val != null)
            {
                if (val === ProjectAcceptance.Rejected.toString())
                {
                    return "table-danger";
                }
                else if (val === ProjectAcceptance.TBD.toString())
                {
                    return "table-warning";
                }
                else if (val === ProjectAcceptance.Accepted.toString())
                {
                    return "table-success";
                }
            }

            return '';
        }

        getCriterionBarColor(c: SelectionCriterionReadModel): string
        {
            return getDistinctBgCss(c.ValueIndex);
        }

        getCriterionColor(c: SelectionCriterionReadModel): string
        {
            return getDistinctColor(c.ValueIndex);
        }

        getAcceptanceButtonCss(p: SelectionProjectReadModel, btn: string) 
        {
            const acceptanceClasses = {
                'Accepted': 'btn btn-sm btn-secondary',
                'Rejected': 'btn btn-sm btn-secondary',
                'TBD': 'btn btn-sm btn-secondary',
            };

            return p.UiAcceptance === btn ? acceptanceClasses[btn] : 'btn btn-sm btn-outline-secondary';
        }


        acceptSelection(p: SelectionProjectReadModel)
        {
            this.$emit('accept-selection', p);
        }

        rejectSelection(p: SelectionProjectReadModel)
        {
            this.$emit('reject-selection', p);
        }

        lockSelection(p: SelectionProjectReadModel)
        {
            this.$emit('lock-selection', p);
        }

        unlockSelection(p: SelectionProjectReadModel)
        {
            this.$emit('unlock-selection', p);
        }
    }

</script>

<template>
    <div>
        <CriterionSelectorDialog :viewModel="criterionSelectorDialog" 
                                 @confirm-selection="criterionId = $event" />

        <nav class="navbar navbar-light navbar-expand-md" style="background-color: rgba(0, 0, 0, 0.03); border: 1px solid rgba(0, 0, 0, 0.125); border-bottom: 0; " role="navigation">
            <ul class="navbar-nav mr-2">
                <li class="dropdown">
                    <div class="navbar-form">
                        <div class="btn-group">
                            <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">{{projectsDataView}}</button>
                            <div class="dropdown-menu">
                                <a class="dropdown-item" @click="projectsDataView = 'CBA'">Cost-Benefit Analysis</a>
                                <a class="dropdown-item" @click="projectsDataView = 'RAG'">Heatmap</a>
                                <a class="dropdown-item" @click="projectsDataView = 'Ranking'">Ranking</a>
                                <a class="dropdown-item" @click="projectsDataView = 'H2H'">Compare scenarios</a>
                            </div>
                        </div>
                    </div>
                </li>
                <li class="dropdown ml-2" v-if="criteriaFilterVisible === true">
                    <div class="navbar-form">
                        <div class="btn-group">
                            <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">{{criteriaFilter}}</button>
                            <div class="dropdown-menu">
                                <a class="dropdown-item" @click="criteriaFilter = 'Objectives'">Objectives (top)</a>
                                <a class="dropdown-item" @click="criteriaFilter = 'Attributes'">Attributes (bottom)</a>
                                <a class="dropdown-item" @click="criteriaFilter = 'All criteria'">All criteria</a>
                            </div>
                        </div>
                    </div>
                </li>
                <li class="dropdown ml-2" v-if="criterionSelectorVisible === true">
                    <div class="navbar-form">
                        <button class="btn btn-outline-secondary" type="button" @click="showCriterionSelector">Criterion</button>
                    </div>
                </li>
                <li class="dropdown ml-2" v-if="valueFilterVisible === true">
                    <div class="navbar-form">
                        <div class="dropdown">
                            <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Value: {{valueFilter ?? 'Any'}}</button>
                            <div class="dropdown-menu" style="max-height: 250px; overflow-y: auto;">
                                <a class="dropdown-item" @click="valueFilter = null">All</a>
                                <h6 class="dropdown-header">By value</h6>
                                <a class="dropdown-item" @click="valueFilter = 75">Above 75</a>
                                <a class="dropdown-item" @click="valueFilter = 50">Above 50</a>
                            </div>
                        </div>
                    </div>
                </li>

            </ul>

            <ul class="navbar-nav ml-auto mr-2">
                <li class="dropdown">
                    <div class="navbar-form">
                        <div class="btn-group">
                            <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Filter: {{projectsFilter}}</button>
                            <div class="dropdown-menu">
                                <a class="dropdown-item" @click="updateProjectsFilter('All')">All</a>
                                <div class="dropdown-divider"></div>
                                <h6 class="dropdown-header">Filter by status:</h6>
                                <a class="dropdown-item" @click="updateProjectsFilter('TBD')">TBD</a>
                                <a class="dropdown-item" @click="updateProjectsFilter('Accepted')">Accepted</a>
                                <a class="dropdown-item" @click="updateProjectsFilter('Rejected')">Rejected</a>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>

            <form class="form-inline my-2 my-lg-0" role="search">
                <input class="form-control mr-sm-2" type="search" placeholder="Search" :value="searchPhrase" @input="updateSearchPhrase($event.target.value)">
            </form>
        </nav>

        <div v-if="projectsDataView === 'CBA'">
            <div class="table-responsive">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th class="align-middle" style="min-width: 300px; width: 300px; max-width: 300px;">Project</th>
                            <th class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px;">P&L</th>
                            <th class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px;">Selection</th>
                            <th class="text-center align-middle" style="min-width: 200px; width: 200px; max-width: 200px;">Cost of value point <br /> ({{currencyLabel}})</th>
                            <th class="text-right align-middle" style="min-width: 250px; width: auto;">Costs <br /> ({{currencyLabel}})</th>
                            <th class="align-middle" style="min-width: 250px; width: auto; ">Value <br /> (0-100)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="a in projects" :key="a.Id">
                            <td style="min-width: 300px; width: 300px; max-width: 300px; ">
                                <a class="text-primary" v-sanitizedHref="a.DashboardUrl">
                                    {{a.Name}}
                                </a>
                            </td>
                            <td class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px; ">
                                <a v-if="a.PlanId" class="text-primary" v-sanitizedHref="a.PnlUrl">
                                    View
                                </a>
                                <a v-else class="text-danger" v-sanitizedHref="a.PlansUrl">
                                    NA
                                </a>
                            </td>
                            <td class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px; ">
                                <div class="btn-group mr-2" role="group">
                                    <button type="button" :class="getAcceptanceButtonCss(a, 'Accepted')" @click="acceptSelection(a)" :disabled="a.UiAcceptanceLocked === true">Accept</button>
                                    <button type="button" :class="getAcceptanceButtonCss(a, 'Rejected')" @click="rejectSelection(a)" :disabled="a.UiAcceptanceLocked === true">Reject</button>
                                </div>
                                <button v-if="a.UiAcceptanceLocked === true" class="btn btn-outline-secondary btn-sm" @click="unlockSelection(a)">
                                    <i class="fa-solid fa-lock"></i>
                                </button>
                                <button v-if="a.UiAcceptanceLocked === false" class="btn btn-outline-secondary btn-sm"
                                        title="Click to lock selection status" :disabled="a.UiAcceptance === 'TBD'" @click="lockSelection(a)">
                                    <i class="fa-solid fa-lock-open"></i>
                                </button>
                            </td>
                            <td class="text-center align-middle" style="min-width: 200px; width: 200px; max-width: 200px;">
                                {{a.CostOfValuePointString}}
                            </td>
                            <td class="text-right align-middle" style="min-width: 250px; width: auto; ">
                                <div class="progress justify-content-end" style="height: 30px;">
                                    <div class="progress-bar bg-danger" role="progressbar" :style="{width: a.NormalizedTotalCost + '%'}">
                                        {{a.TotalCostString}}
                                    </div>
                                </div>
                            </td>
                            <td class="align-middle" style="min-width: 250px; width: auto;">
                                <div class="progress" style="height: 30px;">
                                    <div class="progress-bar bg-success" role="progressbar" :style="{width: a.TotalValue + '%'}">
                                        {{a.TotalValueString}}
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </div>

        <div v-if="projectsDataView === 'RAG'">

            <div class="table-responsive">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th class="align-middle" style="min-width: 300px; width: 300px; max-width: 300px;">Project</th>
                            <th class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px;">P&L</th>
                            <th class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px;">Selection</th>
                            <th class="text-center align-middle" v-for="c in criteria" :key="c.Id" style="min-width: 300px; width: auto;">{{c.Name}}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="a in projects" :key="a.Id">
                            <td style="min-width: 300px; width: 300px; max-width: 300px; ">
                                <a class="text-primary" v-sanitizedHref="a.DashboardUrl">
                                    {{a.Name}}
                                </a>
                            </td>
                            <td class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px; ">
                                <a v-if="a.PlanId" class="text-primary" v-sanitizedHref="a.PnlUrl">
                                    View
                                </a>
                                <a v-else class="text-danger" v-sanitizedHref="a.PlansUrl">
                                    NA
                                </a>
                            </td>
                            <td class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px; ">
                                <div class="btn-group mr-2" role="group">
                                    <button type="button" :class="getAcceptanceButtonCss(a, 'Accepted')" @click="acceptSelection(a)" :disabled="a.UiAcceptanceLocked === true">Accept</button>
                                    <button type="button" :class="getAcceptanceButtonCss(a, 'Rejected')" @click="rejectSelection(a)" :disabled="a.UiAcceptanceLocked === true">Reject</button>
                                </div>
                                <button v-if="a.UiAcceptanceLocked === true" class="btn btn-outline-secondary btn-sm" @click="unlockSelection(a)">
                                    <i class="fa-solid fa-lock"></i>
                                </button>
                                <button v-if="a.UiAcceptanceLocked === false" class="btn btn-outline-secondary btn-sm"
                                        title="Click to lock selection status" :disabled="a.UiAcceptance === 'TBD'" @click="lockSelection(a)">
                                    <i class="fa-solid fa-lock-open"></i>
                                </button>
                            </td>
                            <td class="text-center align-middle" v-for="c in criteria" :key="c.Id" style="min-width: 300px; width: auto;" 
                                :class="getRagClass(a.NormalizedCriteriaValues[c.ValueIndex])">
                                {{formatValue(a.NormalizedCriteriaValues[c.ValueIndex])}}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="projectsDataView === 'H2H'">

            <div class="table-responsive">
                <table class="table table-bordered table-hover">
                    <thead>
                        <tr>
                            <th class="align-middle" style="min-width: 300px; width: 300px; max-width: 300px;">Project</th>
                            <th class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px;">P&L</th>
                            <th class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px;">Selection</th>
                            <th v-for="s in comparableScenarios" :key="s.Id">{{s.Name}}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="a in comparableProjects" :key="a.Id">
                            <td style="min-width: 300px; width: 300px; max-width: 300px; ">
                                <a class="text-primary" v-sanitizedHref="a.DashboardUrl">
                                    {{a.Name}}
                                </a>
                            </td>
                            <td class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px; ">
                                <a v-if="a.PlanId" class="text-primary" v-sanitizedHref="a.PnlUrl">
                                    View
                                </a>
                                <a v-else class="text-danger" v-sanitizedHref="a.PlansUrl">
                                    NA
                                </a>
                            </td>
                            <td class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px; ">
                                <div class="btn-group mr-2" role="group">
                                    <button type="button" :class="getAcceptanceButtonCss(a, 'Accepted')" @click="acceptSelection(a)" :disabled="a.UiAcceptanceLocked === true">Accept</button>
                                    <button type="button" :class="getAcceptanceButtonCss(a, 'Rejected')" @click="rejectSelection(a)" :disabled="a.UiAcceptanceLocked === true">Reject</button>
                                </div>
                                <button v-if="a.UiAcceptanceLocked === true" class="btn btn-outline-secondary btn-sm" @click="unlockSelection(a)">
                                    <i class="fa-solid fa-lock"></i>
                                </button>
                                <button v-if="a.UiAcceptanceLocked === false" class="btn btn-outline-secondary btn-sm"
                                        title="Click to lock selection status" :disabled="a.UiAcceptance === 'TBD'" @click="lockSelection(a)">
                                    <i class="fa-solid fa-lock-open"></i>
                                </button>
                            </td>
                            <td v-for="s in comparableScenarios" :key="s.Id" class="align-middle" style="min-width: 250px; width: auto;"
                                :class="getCompareAcceptanceClass(a.ComparableScenariosAcceptance[s.ValueIndex])">
                                {{a.ComparableScenariosAcceptance[s.ValueIndex]}}
                            </td>

                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="projectsDataView === 'Ranking'">
            <table class="table table-bordered table-hover mt-2">
                <thead>
                    <tr>
                        <th class="align-middle" style="min-width: 300px; width: 300px; max-width: 300px;">Project</th>
                        <th class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px;">P&L</th>
                        <th class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px;">Selection</th>
                        <th class="text-center align-middle" style="min-width: 150px; width: 150px; max-width: 150px;">Value</th>
                        <th class="align-middle" style="min-width: 250px; width: auto;">
                            {{criterionLabel}}
                            <div class="float-right" v-if="selectedCriterion.IsBottom === false">
                                <div class="dropdown d-inline mr-2">
                                    <button data-toggle="dropdown" class="btn btn-secondary btn-sm">
                                        Legend
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right" role="menu" style="width: 300px;">
                                        <span class="dropdown-item-text font-weight-normal" v-for="c in subcriteria" :key="c.Id">
                                            <i class="fa fa-square" aria-hidden="true" :style="{color: getCriterionColor(c)}"></i>
                                            {{c.Name}}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="a in rankingProjects" :key="a.Id">
                        <td style="min-width: 300px; width: 300px; max-width: 300px; ">
                            <a class="text-primary" v-sanitizedHref="a.DashboardUrl">
                                {{a.Name}}
                            </a>
                        </td>
                        <td class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px; ">
                            <a v-if="a.HasActivePlan" class="text-primary" v-sanitizedHref="a.PnlUrl">
                                View
                            </a>
                            <a v-else class="text-danger" v-sanitizedHref="a.PlansUrl">
                                NA
                            </a>
                        </td>
                        <td class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px; ">
                            <div class="btn-group mr-2" role="group">
                                <button type="button" :class="getAcceptanceButtonCss(a, 'Accepted')" @click="acceptSelection(a)" :disabled="a.UiAcceptanceLocked === true">Accept</button>
                                <button type="button" :class="getAcceptanceButtonCss(a, 'Rejected')" @click="rejectSelection(a)" :disabled="a.UiAcceptanceLocked === true">Reject</button>
                            </div>
                            <button v-if="a.UiAcceptanceLocked === true" class="btn btn-outline-secondary btn-sm" @click="unlockSelection(a)">
                                <i class="fa-solid fa-lock"></i>
                            </button>
                            <button v-if="a.UiAcceptanceLocked === false" class="btn btn-outline-secondary btn-sm"
                                    title="Click to lock selection status" :disabled="a.UiAcceptance === 'TBD'" @click="lockSelection(a)">
                                <i class="fa-solid fa-lock-open"></i>
                            </button>
                        </td>
                        <td class="text-center align-middle" style="min-width: 150px; width: 150px; max-width: 150px;">
                            {{formatValue(a.NormalizedCriteriaValues[selectedCriterion.ValueIndex])}}
                        </td>
                        <td>
                            <div v-if="!selectedCriterion.IsBottom" class="progress" style="height: 30px;">
                                <div v-for="c in subcriteria" :key="c.Id" :title="c.Name" class="progress-bar" role="progressbar" :class="getCriterionBarColor(c)"
                                     :style="{width: a.LocalWeightedCriteriaValues[c.ValueIndex] + '%'}"></div>
                            </div>
                            <div v-if="selectedCriterion.IsBottom" class="progress" style="height: 30px;">
                                <div class="progress-bar" role="progressbar" :style="{width: a.NormalizedCriteriaValues[selectedCriterion.ValueIndex] + '%'}"></div>
                            </div>
                        </td>

                    </tr>
                </tbody>
            </table>


        </div>


    </div>
</template>      


<style scoped>




</style>